import { useState } from "react";
import { Check, Copy, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { Spinner } from "../components/Spinner.tsx";
import { db } from "../db.ts";
import { id, tx } from "@instantdb/react";
import { Input, Label } from "../components/ui/index.ts";

const generateSecret = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

export const AdminPartners = () => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const partnersQuery = db.useQuery({ partners: {} });

  if (partnersQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (partnersQuery.error) {
    return (
      <div className="text-red-400 p-4">{partnersQuery.error.message}</div>
    );
  }

  const { partners } = partnersQuery.data;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const cleanName = name.trim();
    if (!cleanName) {
      setError("Please enter a partner name");
      return;
    }
    if (
      partners.some((p) => p.name.toLowerCase() === cleanName.toLowerCase())
    ) {
      setError("A partner with this name already exists.");
      return;
    }

    const newId = id();
    db.transact(
      tx.partners[newId].update({
        name: cleanName,
        secret: generateSecret(),
        createdAt: Date.now(),
      }),
    ).then(() => {
      setName("");
      setShowSecrets((prev) => ({ ...prev, [newId]: true }));
    }).catch((err) => {
      setError(`Failed to save: ${err.message}`);
    });
  };

  const handleDelete = (partnerId: string, partnerName: string) => {
    if (
      confirm(
        `Delete partner "${partnerName}"? Their webhook secret will stop working immediately.`,
      )
    ) {
      db.transact(tx.partners[partnerId].delete());
    }
  };

  const toggleShowSecret = (partnerId: string) => {
    setShowSecrets((prev) => ({ ...prev, [partnerId]: !prev[partnerId] }));
  };

  const copySecret = (partnerId: string, secret: string) => {
    navigator.clipboard.writeText(secret).then(() => {
      setCopiedId(partnerId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="space-y-8 p-4">
      {/* Form Card */}
      <div className="bg-gray-900 border border-white/10 rounded-xl p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Plus size={20} className="text-purple-500" />
          Add New Partner
        </h3>

        <form onSubmit={handleAdd} className="space-y-4">
          {error && (
            <div className="bg-red-900/40 border border-red-500/50 text-red-200 text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <Label className="mb-2">
              Partner Name
            </Label>
            <Input
              type="text"
              placeholder="e.g. Selector"
              value={name}
              onChange={(e) =>
                setName((e.target as unknown as { value: string }).value)}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              A webhook secret is generated automatically. Share it with the
              partner securely — it authenticates their <code>/submit</code> and
              {" "}
              <code>/submit-structured</code> requests via the{" "}
              <code>x-signature</code> HMAC header.
            </p>
          </div>

          <button
            type="submit"
            className="w-full md:w-auto bg-purple-600 hover:bg-purple-700 text-white font-medium px-6 py-2.5 rounded-lg transition-all flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            Add Partner
          </button>
        </form>
      </div>

      {/* List Card */}
      <div className="bg-gray-900 border border-white/10 rounded-xl p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center justify-between">
          <span>Configured Partners</span>
          <span className="text-sm font-normal text-gray-500">
            {partners.length} partner{partners.length === 1 ? "" : "s"}
          </span>
        </h3>

        {partners.length === 0
          ? (
            <div className="text-center text-gray-500 py-12 border border-dashed border-white/10 rounded-lg">
              No partners configured yet.
            </div>
          )
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {partners.map((partner) => {
                const isShowing = !!showSecrets[partner.id];
                return (
                  <div
                    key={partner.id}
                    className="bg-black/40 border border-white/10 rounded-lg p-5 flex flex-col justify-between hover:border-white/20 transition-all space-y-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-lg font-bold text-white">
                          {partner.name}
                        </div>
                        {partner.createdAt
                          ? (
                            <div className="text-xs text-gray-500 mt-1">
                              Added{" "}
                              {new Date(partner.createdAt).toLocaleDateString()}
                            </div>
                          )
                          : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(partner.id, partner.name)}
                        className="text-gray-500 hover:text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg transition"
                        title="Delete partner"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="space-y-2 bg-black/50 border border-white/5 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">
                          Webhook Secret
                        </span>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() =>
                              copySecret(partner.id, partner.secret)}
                            className="text-gray-400 hover:text-white transition flex items-center gap-1 text-xs"
                          >
                            {copiedId === partner.id
                              ? (
                                <>
                                  <Check size={12} />
                                  Copied
                                </>
                              )
                              : (
                                <>
                                  <Copy size={12} />
                                  Copy
                                </>
                              )}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleShowSecret(partner.id)}
                            className="text-gray-400 hover:text-white transition flex items-center gap-1 text-xs"
                          >
                            {isShowing
                              ? (
                                <>
                                  <EyeOff size={12} />
                                  Hide
                                </>
                              )
                              : (
                                <>
                                  <Eye size={12} />
                                  Show
                                </>
                              )}
                          </button>
                        </div>
                      </div>
                      <div className="font-mono text-xs text-gray-300 break-all select-all">
                        {isShowing
                          ? partner.secret
                          : "••••••••••••••••••••••••••••••••"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
};
